import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedThemeMode = 'light' | 'dark';

interface ThemeModeContextValue {
  mode: ThemeMode;
  resolvedMode: ResolvedThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'gc_theme_mode_v1';
const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system'];
const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

const isThemeMode = (value: string | null): value is ThemeMode =>
  !!value && THEME_MODES.includes(value as ThemeMode);

const getSystemThemeMode = (): ResolvedThemeMode => {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
};

const readStoredThemeMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeMode(stored)) return stored;
  } catch {
    /* localStorage may be unavailable in private mode */
  }
  return 'system';
};

const persistThemeMode = (mode: ThemeMode): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore quota / private mode failures */
  }
};

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredThemeMode());
  const [systemMode, setSystemMode] = useState<ResolvedThemeMode>(() =>
    getSystemThemeMode()
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemMode(getSystemThemeMode());

    handleChange();
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  const resolvedMode = mode === 'system' ? systemMode : mode;

  useEffect(() => {
    persistThemeMode(mode);
    document.documentElement.dataset.gcTheme = resolvedMode;
    document.documentElement.dataset.gcThemePreference = mode;
  }, [mode, resolvedMode]);

  const setMode = useCallback((nextMode: ThemeMode) => {
    setModeState(nextMode);
  }, []);

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};
